import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import { Drawer } from '@mui/material';
import { chatActions, tagActions } from 'store';
import { SimpleTooltip } from 'components';
import { navBarStyles } from './style';
import { globalSessionENums, Images, Images as images, PermissionList, RolePermission, SaveParams, tagEnums, useModal, User } from 'utils';
import { getToken } from 'utils/crypto';
import { getSocket } from 'utils/socketManager';
import { MyProfile } from '../../myProfile';
import { NotificationList } from '../../notification';
import { ChatDrawer } from '../../chat';
import { CreateTask } from '../../../pages/tasks/fragments';
import { WarningDrawer, ApptDrawer } from './common';
import { ClientsStaffMap } from '../../clientsStaffMap';

export const UserInfo = ({}) => {
    const classes = navBarStyles();
    const dispatch = useDispatch();
    const { userInfo } = User();
    const unRenderNotifications = useSelector((state) => state.notifications.unRenderNotifications);
    const therapySession = useSelector((state) => state.auth.therapySessionEvents?.[globalSessionENums.SESSION_EXPIRED]);
    const chats = useSelector((state) => state.chat.chats);
    const totalChatUnread = chats?.reduce((sum, c) => sum + (c.unread || 0), 0) || 0;
    const [drawerPosition, setDrawerPosition] = useState({ right: false });
    const [drawerNotificationPosition, setDrawerNotificationPosition] = useState({ right: false });
    const [drawerChatPosition, setDrawerChatPosition] = useState({ right: false });
    const [drawerWarningPosition, setDrawerWarningPosition] = useState({ right: false });
    const [drawerApptPosition, setDrawerApptPosition] = useState({ right: false });
    const [drawerMapPosition, setDrawerMapPosition] = useState({ right: false });
    const globalHandlersRef = useRef({});
    const { open } = useModal();
    const location = useLocation();
    const info = location?.state;
    const navigate = useNavigate();

    // Load chats on mount so the unread badge is populated immediately
    useEffect(() => {
        if (userInfo?.id) {
            dispatch(chatActions.getChats('noLoad'));
        }
    }, []);

    // Initialize socket and register global listeners on mount.
    // Reads token fresh from localStorage so this works on page refresh,
    // new tabs, bookmarks, and back-button navigation — not just login.
    useEffect(() => {
        const authToken = getToken();
        if (!authToken) return;
        const socket = getSocket(authToken);
        if (!socket) return;

        globalHandlersRef.current = {
            onUnreadUpdate: (data) => dispatch(chatActions.chatUnreadUpdate(data)),
            onNewChat: () => dispatch(chatActions.getChats('noLoad')),
            onChatDeleted: (data) => dispatch(chatActions.chatDeletedEvent(data)),
            onMemberRemoved: (data) => dispatch(chatActions.chatMemberRemovedEvent(data)),
            onMembersAdded: (data) => dispatch(chatActions.chatMembersAddedEvent(data)),
        };

        socket.on('chat:unread-update', globalHandlersRef.current.onUnreadUpdate);
        socket.on('chat:new-chat', globalHandlersRef.current.onNewChat);
        socket.on('chat:chat-deleted', globalHandlersRef.current.onChatDeleted);
        socket.on('chat:member-removed', globalHandlersRef.current.onMemberRemoved);
        socket.on('chat:members-added', globalHandlersRef.current.onMembersAdded);

        return () => {
            socket.off('chat:unread-update', globalHandlersRef.current.onUnreadUpdate);
            socket.off('chat:new-chat', globalHandlersRef.current.onNewChat);
            socket.off('chat:chat-deleted', globalHandlersRef.current.onChatDeleted);
            socket.off('chat:member-removed', globalHandlersRef.current.onMemberRemoved);
            socket.off('chat:members-added', globalHandlersRef.current.onMembersAdded);
        };
    }, []);

    const toggleDrawer = (anchor, open, modal) => {
        setDrawerPosition({ ...drawerPosition, [anchor]: open });
    };

    const notificationDrawer = (anchor, open, modal) => {
        setDrawerNotificationPosition({ ...drawerPosition, [anchor]: open });
    };

    const chatDrawer = (anchor, open) => {
        setDrawerChatPosition({ ...drawerChatPosition, [anchor]: open });
    };

    const warningDrawer = (anchor, open) => {
        setDrawerWarningPosition({ ...drawerWarningPosition, [anchor]: open });
    };

    const apptDrawer = (anchor, open) => {
        setDrawerApptPosition({ ...drawerApptPosition, [anchor]: open });
    };

    const mapDrawer = (anchor, open) => {
        setDrawerMapPosition({ ...drawerMapPosition, [anchor]: open });
    };

    const handleCreateTask = () => {
        dispatch(tagActions.getTags({ module: tagEnums.TASK }));
        open(
            <CreateTask
                afterSuccess={() => {
                    if (location?.pathname === '/tasks') {
                        SaveParams(navigate, info, );
                    }
                }}
            />
        );
    };

    return (
        <div style={{ color: 'black', position: 'relative' }} className={classes.boxWrapper}>
            {RolePermission([PermissionList.TASK_MANAGE?.code, PermissionList.TASK_FULL_ACCESS?.code]) && (
                <SimpleTooltip title="Create Task" placement="bottom">
                    <div onClick={handleCreateTask} className={classes.notificationBox}>
                        <img src={Images.plusSquare} alt="Create Task" />
                    </div>
                </SimpleTooltip>
            )}

            {RolePermission([
                PermissionList.CLIENT_LIST_READ?.code,
                PermissionList.STAFF_LIST_READ?.code,
                PermissionList.CONNECTED_CLIENT_READ?.code,
            ]) && (
                <SimpleTooltip title="Clients & Staff Map" placement="bottom">
                    <div onClick={() => mapDrawer('right', true)} className={classes.notificationBox}>
                        <img src={Images.map} alt="Clients & Staff Map" />
                    </div>
                </SimpleTooltip>
            )}

            <SimpleTooltip title="Upcoming Appointments" placement="bottom">
                <div onClick={() => apptDrawer('right', true)} className={classes.notificationBox}>
                    <img src={Images.calendarToday} alt="Upcoming Appointments" />
                </div>
            </SimpleTooltip>

            <SimpleTooltip title="Issues" placement="bottom">
                <div onClick={() => warningDrawer('right', true)} className={classes.notificationBox}>
                    {therapySession?.evenType === globalSessionENums.SESSION_EXPIRED && therapySession?.data?.length > 0 && (
                        <div className={classes.noteCount} style={{ background: '#E38635' }}>
                            {therapySession?.data?.length > 9 ? '9+' : therapySession?.data?.length}
                        </div>
                    )}
                    <img
                        src={Images.alertWarning}
                        alt="Issues"
                        style={{
                            width: 20,
                            height: 20,
                            filter: 'brightness(0) saturate(100%) invert(81%) sepia(21%) saturate(7469%) hue-rotate(332deg) brightness(90%) contrast(97%)',
                        }}
                    />
                </div>
            </SimpleTooltip>

            <SimpleTooltip title="Chat" placement="bottom">
                <div onClick={() => chatDrawer('right', true)} className={classes.notificationBox}>
                    {totalChatUnread > 0 && <div className={classes.noteCount}>{totalChatUnread > 9 ? '9+' : totalChatUnread}</div>}
                    <img src={Images.chatIcon} alt="Chat" />
                </div>
            </SimpleTooltip>

            <SimpleTooltip title="Notifications" placement="bottom">
                <div onClick={() => notificationDrawer('right', true)} className={classes.notificationBox}>
                    {unRenderNotifications > 0 && (
                        <div className={classes.noteCount}>{unRenderNotifications > 9 ? '9+' : unRenderNotifications}</div>
                    )}
                    <img src={Images.notification} alt="Notifications" />
                </div>
            </SimpleTooltip>

            <div onClick={() => toggleDrawer('right', true)} className={classes.userInfo}>
                <img src={userInfo?.avatar?.url ? userInfo?.avatar?.url : images.userProfile} alt="Avatar" />
                <div className={classes.online} />
            </div>

            <Drawer
                sx={{ zIndex: 1300 }}
                className={classes.myProfileDrawerStyle}
                anchor={'right'}
                open={drawerPosition.right}
                onClose={() => toggleDrawer('right', false)}>
                <MyProfile info={userInfo} />
            </Drawer>

            <Drawer
                sx={{ zIndex: 1300 }}
                className={classes.myProfileDrawerStyle}
                anchor={'right'}
                open={drawerNotificationPosition.right}
                onClose={() => notificationDrawer('right', false)}>
                <NotificationList handleClose={() => notificationDrawer('right', false)} info={userInfo} />
            </Drawer>

            <Drawer
                sx={{ zIndex: 1300 }}
                className="chat-drawer-paper"
                anchor={'right'}
                open={drawerChatPosition.right}
                onClose={() => chatDrawer('right', false)}>
                <ChatDrawer handleClose={() => chatDrawer('right', false)} />
            </Drawer>

            <Drawer
                sx={{ zIndex: 1300 }}
                className="simple-drawer-paper"
                anchor={'right'}
                open={drawerWarningPosition.right}
                onClose={() => warningDrawer('right', false)}>
                <WarningDrawer handleClose={() => warningDrawer('right', false)} />
            </Drawer>

            <Drawer
                sx={{ zIndex: 1300 }}
                className="simple-drawer-paper"
                anchor={'right'}
                open={drawerApptPosition.right}
                onClose={() => apptDrawer('right', false)}>
                <ApptDrawer handleClose={() => apptDrawer('right', false)} />
            </Drawer>

            <Drawer
                sx={{ zIndex: 1300 }}
                className="map-drawer-paper"
                anchor={'right'}
                open={drawerMapPosition.right}
                onClose={() => mapDrawer('right', false)}>
                <ClientsStaffMap handleClose={() => mapDrawer('right', false)} />
            </Drawer>
        </div>
    );
};
